import React from 'react';
import { Field, FieldArray, Form, SubmissionError } from 'redux-form';
// @ts-ignore
import { diff } from 'deep-object-diff';
import omit from 'lodash/omit';

import { ToggleFieldComponent } from 'components/form/toggle/toggleField.component';
import AppModal from 'components/modal/appModal';
import { AppSelectField } from 'components/form/appSelectField';
import { AppTextField } from 'components/form/appTextField';
import { AppTextAreaField } from 'components/form/appTextAreaField';
import { maxLength, required, requiredString, youtubePlaylistValidator } from 'components/form/formValidations';
import { ColorPickerFieldComponent } from 'components/form/color-picker/color-picker-field.component';
import {
  announcementSectionToggleField,
  socialMessagingText,
  useColorField,
  useSocialMessagingTextField,
  virtualGraduationMessageField,
  virtualGraduationSectionToggleField,
  virtualGraduationVideoField,
  customContentSectionToggleField,
  customContentTextField,
} from './PortalFormContainer';
import { DomainFieldRow } from './domainFieldsRow/DomainFieldsRow';
import { ClientOption, FormFieldsTypes, FormValuesFields, PortalFormProps } from './PortalForm.types';
import formStyles from 'components/form/appForm.module.scss';
import { MarkdownField } from './markdownField/MarkdownField';
import { DomainField } from './domainField/DomainField';
import { FileField } from 'components/form/fileField';
import { fileField, validateImg } from './PortalForm.utils';

const socialMessagingTextValidator = [maxLength(150), required];
const inputFieldValidator = maxLength(255);
const announcementMessageValidator = [maxLength(1000), required];
const customContentTextValidator = [maxLength(4000), required];

export const PortalForm = ({
  handleSubmit,
  form,
  onSubmit,
  clientsOptions,
  pristine,
  change,
  id,
  title,
  buttonIcon,
  hideOpenButton,
  useColor,
  reset,
  error,
  useSocialMessagingText,
  onClientChange,
  resetInitialValues,
  virtualGraduationSectionToggle: virtualGraduationSectionToggleValue,
  virtualGraduationMessage: virtualGraduationMessageValue,
  formMeta,
  customContentSectionToggle: customContentSectionToggleValue,
  customContentText: customContentTextValue,
  announcementSectionToggle: announcementSectionToggleValue,
  formInitialValues,
  announcementMessage,
}: PortalFormProps) => {
  const handleOnSubmit = ({
    useColor: useColorValue,
    color,
    useSocialMessagingText: useSocialMessagingTextValue,
    socialMessagingText: socialMessagingTextValue,
    customContentBackgroundImageUrl,
    heroBackgroundImageUrl,
    domain,
    alternativeDomains,
    virtualGraduationSectionToggle,
    virtualGraduationMessage,
    logoImageUrl,
    announcementSectionToggle,
    announcementCategory,
    ...values
  }: FormValuesFields) => {
    if (pristine) {
      throw new SubmissionError({
        _error: 'At least one field is required to change in order to submit the form.',
      });
    }

    const changedValues = diff(
      omit(formInitialValues, [
        useColorField,
        useSocialMessagingTextField,
        socialMessagingText,
        'color',
        'clientId',
        'domain',
        'customContentBackgroundImageUrl',
        'heroBackgroundImageUrl',
        'alternativeDomains',
        'virtualGraduationMessage',
        'logoImageUrl',
        'announcementCategory',
      ]),
      { ...values, virtualGraduationSectionToggle, announcementSectionToggle },
    ) as FormFieldsTypes;

    onSubmit({
      domain: domain ? `${domain}.${process.env.REACT_APP_PORTALS_DOMAIN}` : domain,
      alternativeDomains: (alternativeDomains || []).map(prefix =>
        prefix ? `${prefix}.${process.env.REACT_APP_PORTALS_DOMAIN}` : prefix,
      ),
      color: (useColorValue && color) || null,
      socialMessagingText: (useSocialMessagingTextValue && socialMessagingTextValue) || null,
      ...(announcementSectionToggle && { announcementCategory }),
      ...fileField(logoImageUrl, formInitialValues.logoImageUrl, 'logoImageUrl'),
      ...(virtualGraduationSectionToggle && { virtualGraduationMessage }),
      ...fileField(
        customContentBackgroundImageUrl,
        formInitialValues.customContentBackgroundImageUrl,
        'customContentBackgroundImageUrl',
      ),
      ...fileField(heroBackgroundImageUrl, formInitialValues.heroBackgroundImageUrl, 'heroBackgroundImageUrl'),
      ...changedValues,
    });
  };

  return (
    <AppModal
      id={id}
      title={title}
      buttonIcon={buttonIcon}
      form={form}
      hideOpenButton={hideOpenButton}
      onClosed={() => {
        reset();
        resetInitialValues();
      }}
    >
      <Form id={form} onSubmit={handleSubmit(handleOnSubmit)} className={formStyles.appForm}>
        {error && <span className={formStyles.error}>{error}</span>}
        <AppSelectField
          name="clientId"
          key="clientId"
          label="Client"
          options={clientsOptions}
          validate={[required, requiredString]}
          onChangeCallback={({ value }: ClientOption) => onClientChange(value)}
        />
        <DomainField name="domain" label="Domain" />
        <ToggleFieldComponent name={useColorField} label="Use this colour" />
        {useColor && <ColorPickerFieldComponent name="color" label="Clip Site colour" />}
        <AppTextField name="socialMediaMessage" label="Social Media Message" validate={[inputFieldValidator]} />
        <ToggleFieldComponent name={useSocialMessagingTextField} label="Use Social Messaging Text" />
        {useSocialMessagingText && (
          <AppTextAreaField
            name={socialMessagingText}
            label="Social Messaging Text"
            validate={socialMessagingTextValidator}
          />
        )}
        <ColorPickerFieldComponent name="logoBackgroundColor" label="Client logo background" />
        <ToggleFieldComponent name={virtualGraduationSectionToggleField} label="Use Virtual Graduation Section" />
        {virtualGraduationSectionToggleValue && (
          <>
            <MarkdownField
              name={virtualGraduationMessageField}
              value={virtualGraduationMessageValue}
              onMarkdownChange={change}
              label="Virtual Graduation Message"
              required
              isFieldTouched={formMeta.virtualGraduationMessage?.touched}
              validate={[required]}
            />
            <AppTextField
              name={virtualGraduationVideoField}
              label="Virtual Graduation Video"
              validate={[youtubePlaylistValidator]}
            />
          </>
        )}
        <ToggleFieldComponent name={customContentSectionToggleField} label="Custom content Section" />
        {customContentSectionToggleValue && (
          <>
            <AppTextField name="customContentTitle" label="Title" validate={[required, inputFieldValidator]} />
            <MarkdownField
              name={customContentTextField}
              value={customContentTextValue}
              onMarkdownChange={change}
              label="Custom Message Section"
              required
              isFieldTouched={formMeta.customContentText?.touched}
              validate={customContentTextValidator}
            />
            <ColorPickerFieldComponent name="customContentBackgroundColor" label="Background colour" />
            <Field
              name="customContentBackgroundImageUrl"
              label="Background image"
              extensions={['png', 'jpg', 'gif', 'svg']}
              component={FileField as any}
              change={change}
              validate={[validateImg]}
            />
          </>
        )}
        <Field
          name="heroBackgroundImageUrl"
          label="Hero background"
          extensions={['png', 'jpg', 'gif', 'svg']}
          component={FileField as any}
          change={change}
          validate={[validateImg]}
        />
        <Field
          name="logoImageUrl"
          label="Portal logo"
          extensions={['png', 'jpg', 'gif', 'svg']}
          component={FileField as any}
          change={change}
          validate={[validateImg]}
        />
        <AppTextField name="gaTrackingId" label="Google Analytics ID" validate={required} />
        <ToggleFieldComponent name="robotsIndexingToggle" label="Robots indexing" />
        <ToggleFieldComponent name="clipSearchEnabled" label="Clip search" />
        <ToggleFieldComponent name={announcementSectionToggleField} label="Site Announcements Section" />
        {announcementSectionToggleValue && (
          <>
            <AppSelectField
              name="announcementCategory"
              label="Category"
              validate={[required]}
              options={[
                { label: 'Green', value: 'green' },
                { label: 'Orange', value: 'orange' },
                { label: 'Red', value: 'red' },
              ]}
            />
            <MarkdownField
              name="announcementMessage"
              value={announcementMessage}
              onMarkdownChange={change}
              label="Site Announcement Message"
              required
              isFieldTouched={formMeta.announcementMessage?.touched}
              validate={announcementMessageValidator}
            />
          </>
        )}
        <FieldArray name="alternativeDomains" component={DomainFieldRow} />
      </Form>
    </AppModal>
  );
};
